import { Params } from 'react-router';
import { proStudioService, sendProStudioEvent } from '../xstate/app.xstate';

import { isProduction } from '@pypestream/utils';

import { isSelectedBotStale } from './selected-bot-freshness';

export const syncXstateWithRouteParams = ({
  params,
  url: _url,
  debugMode = !isProduction,
}: {
  url: URL;
  params: Params<string>;
  debugMode?: boolean;
}) => {
  const {
    projectId: projectIdParam,
    botId: botIdParam,
    channelId: channelIdParam,
  } = params;

  const projectId = projectIdParam;
  const proStudioContext = proStudioService.getSnapshot().context;

  const {
    botName: botNameCtx,
    channelId: channelIdCtx,
    customerId: customerIdCtx,
  } = {
    botName: proStudioContext.botName,
    channelId: proStudioContext.channelId,
    customerId: proStudioContext.customerId,
  };

  const solutionId = proStudioContext.solutions?.find(
    (solution) => solution.id === botIdParam
  )?.name;

  // A route WITH a projectId but WITHOUT a botId is project-level (`/:projectId`,
  // `/:projectId/settings/*`, `/:projectId/configurations`): it names no solution,
  // so any solution still in context belongs to the page the user just left.
  // Nothing below can clear it — every field is assigned as
  // `event.payload.x || ctx.x` — which is why leaving a solution for Project
  // Settings kept the old solution in the breadcrumb and in the Reporting API
  // Credential modal's channel filter, while a reload of the same URL was fine.
  //
  // Dispatched BEFORE `setParams` so that if this navigation also re-enters
  // `#checkingUserInfo` (a changed projectId), it does so with the scope already
  // cleared. Guarded on there being something to clear, so ordinary navigation
  // inside the project area dispatches nothing.
  //
  // The `/` project-selection screen and `/performance` have no projectId and are
  // intentionally excluded: they aren't inside a project, and nothing there reads
  // the solution scope.
  const isProjectScopedRoute = !!projectId && !botIdParam;
  const hasSolutionScope =
    !!proStudioContext.botName ||
    !!proStudioContext.botId ||
    !!proStudioContext.botIdURLParam ||
    !!proStudioContext.channelId ||
    !!proStudioContext.selectedBot;

  if (isProjectScopedRoute && hasSolutionScope) {
    sendProStudioEvent({ type: 'clearSolutionScope' });
  }

  // PSTUDIO-4121. The SAME defect between two solutions, and the same remedy.
  // A browser POP (Back / trackpad swipe) from solution B to solution A
  // dispatches no `setSelectedSolution`, so `setParams` below rewrites
  // `botName` to A while `selectedBot`, `botId` and `channelId` keep describing
  // B — `setParams` can only ever SET.
  //
  // `loadBotVersions` distrusts that copy for its own three decisions, but it
  // cannot help the other readers of `ctx.selectedBot` (the breadcrumb, Versions,
  // Channels, Files, both publish modals, the delete-solution modal), and it
  // cannot help the window between this dispatch and its own `onDone` — five
  // parallel requests plus `getVersionById` — during which `builder.tsx`
  // composes the identity from the FRESH `botName` and B's `draftVersion` and
  // writes that hybrid into `tableStore.solutionVersionId`. In that window
  // `use-cross-tab-node-sync` compares `buildSolutionVersionId(ctx)` against the
  // store's copy, both are the same hybrid, and a sibling tab's broadcast runs a
  // reload against a version that does not exist.
  //
  // The predicate is the one the invoke uses, so the two levels cannot drift on
  // what "stale" means. The dropdown path is untouched BY CONSTRUCTION:
  // `setSelectedSolution` assigns `selectedBot` and `botName` together before it
  // navigates, so by the time this loader runs the two names already agree and
  // the predicate answers false. `solutionId` is required explicitly because the
  // predicate treats an unnamed context as stale, and a `botId` the loaded
  // `solutions` list cannot resolve yet is "not known", not "different".
  const isStaleSolutionScope =
    !!solutionId &&
    isSelectedBotStale({
      selectedBot: proStudioContext.selectedBot,
      botName: solutionId,
    });

  const clearsStaleSolutionScope =
    !isProjectScopedRoute && isStaleSolutionScope;

  if (clearsStaleSolutionScope) {
    sendProStudioEvent({ type: 'clearSolutionScope' });
  }

  const customerId = customerIdCtx;

  // botIdURLParam is informational — populated on every call so the reducer
  // can resolve a solution name from the URL even if no other field changed.
  // On its own it is not a reason to dispatch `setParams` (that would
  // re-enter '#checkingUserInfo' on every navigation for nothing).
  const eventPayload: {
    projectId?: string;
    botName?: string;
    channelId?: string;
    debugMode?: boolean;
    botIdURLParam?: string;
  } = {
    botIdURLParam: solutionId || botIdParam,
  };

  if (projectId && projectId !== proStudioContext.project?.id) {
    eventPayload.projectId = projectId;
  }

  if (debugMode !== proStudioContext.debugMode) {
    eventPayload.debugMode = debugMode;
  }

  // `botNameCtx` / `channelIdCtx` were read BEFORE the clear above, so after one
  // they describe values the context no longer holds. Re-assert what the route
  // names even when it matched: otherwise a clear that found the RIGHT solution
  // behind an unnamed copy left `botName` empty, and the builder never loaded.
  if (solutionId && (clearsStaleSolutionScope || botNameCtx !== solutionId)) {
    eventPayload.botName = solutionId;
  }

  if (
    projectId &&
    customerId &&
    (clearsStaleSolutionScope ? !!solutionId : !!botNameCtx) &&
    channelIdParam &&
    (clearsStaleSolutionScope || channelIdCtx !== channelIdParam)
  ) {
    eventPayload.channelId = channelIdParam;
  }

  // Previously this was gated by a module-level `let shouldSend` flag. That
  // was a hidden anti-pattern: the variable survived across calls (test
  // isolation, HMR, hypothetical SSR, reentrancy through XState → router →
  // loader) and could trigger a redundant dispatch on a no-op call.
  // Gate locally by what the payload actually contains instead.
  const hasMeaningfulChanges =
    'projectId' in eventPayload ||
    'debugMode' in eventPayload ||
    'botName' in eventPayload ||
    'channelId' in eventPayload;

  if (hasMeaningfulChanges) {
    sendProStudioEvent({ type: 'setParams', payload: eventPayload });
  }
};
